import React, { useMemo } from 'react';
import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd';
import { CspmSourceDocumentSelect } from '@/components/cspm/CspmSourceDocumentSelect';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import { useWorkOrders } from '@/hooks/useWorkOrder';
import {
  CSPM_ALLOCATION_TARGET_TYPES,
  type CspmAllocationTargetType,
  type CspmSourceType,
} from '@/constants/cspm';

const COMMERCIAL_TARGET_TYPES = new Set([
  'opportunity',
  'production_request',
  'sales_order',
  'contract_review',
  'epc_contract',
]);

export type CspmAllocationTargetSelectProps = Omit<
  SelectProps<string>,
  'options' | 'loading' | 'showSearch' | 'optionFilterProp'
> & {
  targetType?: CspmAllocationTargetType | string | null;
  customerId?: string | null;
};

/**
 * Target document picker for allocate / transfer — commercial sources, work orders, or projects.
 */
export const CspmAllocationTargetSelect: React.FC<CspmAllocationTargetSelectProps> = ({
  targetType,
  customerId,
  allowClear = true,
  placeholder,
  disabled,
  value,
  onChange,
  ...rest
}) => {
  const type = String(targetType || '');

  const { data: workOrders = [], isLoading: loadingWo } = useWorkOrders(
    type === 'work_order' ? { take: 100 } : undefined
  );

  const woOptions = useMemo(() => {
    const list = Array.isArray(workOrders) ? workOrders : [];
    return list.map((wo: { id: string; work_order_number?: string; status?: string }) => ({
      value: wo.id,
      label: `${wo.work_order_number || wo.id}${wo.status ? ` (${wo.status})` : ''}`,
    }));
  }, [workOrders]);

  if (!type || !(CSPM_ALLOCATION_TARGET_TYPES as readonly string[]).includes(type)) {
    return (
      <Select
        {...rest}
        value={value}
        onChange={onChange}
        allowClear={allowClear}
        disabled
        placeholder={placeholder || 'Select target type first'}
        options={[]}
      />
    );
  }

  if (COMMERCIAL_TARGET_TYPES.has(type)) {
    return (
      <CspmSourceDocumentSelect
        {...rest}
        sourceType={type as CspmSourceType}
        customerId={customerId}
        value={value}
        onChange={onChange}
        allowClear={allowClear}
        disabled={disabled}
        placeholder={placeholder || 'Select target document'}
      />
    );
  }

  if (type === 'project') {
    return (
      <ProjectSelector
        value={typeof value === 'string' ? value : undefined}
        customerId={customerId || undefined}
        disabled={disabled}
        allowClear={Boolean(allowClear)}
        placeholder={typeof placeholder === 'string' ? placeholder : 'Select project'}
        onChange={(projectId) => {
          onChange?.(projectId as any, undefined as any);
        }}
      />
    );
  }

  if (type === 'work_order') {
    return (
      <Select
        {...rest}
        value={value}
        onChange={onChange}
        allowClear={allowClear}
        disabled={disabled}
        placeholder={placeholder || 'Select work order'}
        loading={loadingWo}
        showSearch
        optionFilterProp="label"
        options={woOptions}
        notFoundContent={loadingWo ? <Spin size="small" /> : undefined}
      />
    );
  }

  return (
    <Select
      {...rest}
      value={value}
      onChange={onChange}
      allowClear={allowClear}
      disabled={disabled}
      placeholder={placeholder || 'Target document ID'}
      options={[]}
    />
  );
};

export default CspmAllocationTargetSelect;
